// insuranceMatch.js — Layer 2 (v227.7.10)
//
// Single source of truth for "is this doctor in-network for this patient".
// Previously each surface (DoctorsScreen, DoctorProfileScreen, ShareScreen) had
// its own copy that leaned on carrier-name substring matching, which breaks on
// display-name variance (e.g. "UnitedHealthcare" vs "United Healthcare").
//
// The primary key is now the STABLE Stedi payer ID: the patient's resolved
// payer id (stamped by discovery / card scan / typeahead) is matched against the
// doctor's `accepted_payer_ids` (derived server-side from the carriers they
// accept). Carrier-id and name-substring matching are retained as graceful
// fallbacks so nothing regresses for records that predate payer-id resolution.

const _clean = (v) => (v == null ? '' : String(v)).trim()

// All routable payer identifiers the patient record might carry, de-duped.
export function getPatientPayerIds(insurance) {
  if (!insurance || typeof insurance !== 'object') return []
  const ids = [
    insurance.stediPayerId,
    insurance.payerId,
    insurance.primaryPayerId,
    insurance.providerId, // newer ProfileInsurance stores the Stedi id here
  ].map(_clean).filter(Boolean)
  return [...new Set(ids)]
}

// Normalize a doctor record's accepted-insurance shape. The API may return a
// flat providers array (newer) or the raw JSONB { providers, accept_all, ipas }.
function normalizeDoctorInsurance(doctor) {
  const raw = doctor?.accepted_insurance
  let providers = []
  let acceptAll = !!doctor?.accept_all_insurance
  let ipas = Array.isArray(doctor?.accepted_ipas) ? doctor.accepted_ipas : []
  if (Array.isArray(raw)) {
    providers = raw
  } else if (raw && typeof raw === 'object') {
    providers = Array.isArray(raw.providers) ? raw.providers : []
    acceptAll = acceptAll || !!raw.accept_all
    if (!ipas.length && Array.isArray(raw.ipas)) ipas = raw.ipas
  }
  const payerIds = (Array.isArray(doctor?.accepted_payer_ids) ? doctor.accepted_payer_ids : [])
    .map(_clean).filter(Boolean)
  return { providers, acceptAll, ipas, payerIds }
}

export function patientHasInsurance(insurance) {
  if (!insurance || typeof insurance !== 'object') return false
  if (insurance.selfPay) return false
  return !!(
    _clean(insurance.providerId) ||
    _clean(insurance.provider) ||
    _clean(insurance.planName) ||
    getPatientPayerIds(insurance).length
  )
}

// Boolean in-network check.
export function isDoctorInNetwork(doctor, insurance) {
  if (!patientHasInsurance(insurance)) return false
  const { providers, acceptAll, ipas, payerIds: acceptedPayerIds } = normalizeDoctorInsurance(doctor)
  if (acceptAll) return true

  // 1. PRIMARY — stable Stedi payer ID match.
  const patientPayerIds = getPatientPayerIds(insurance)
  if (
    patientPayerIds.length &&
    acceptedPayerIds.some((pid) => patientPayerIds.includes(_clean(pid)))
  ) {
    return true
  }

  // 2. Fallback — curated carrier id.
  const patientCarrierId = _clean(insurance.providerId)
  if (
    patientCarrierId &&
    providers.some((c) => _clean(typeof c === 'string' ? c : c?.id) === patientCarrierId)
  ) {
    return true
  }

  // 3. Fallback — carrier name substring (last resort).
  const carrierName = _clean(insurance.provider).toLowerCase()
  if (
    carrierName &&
    providers.some((c) => {
      const cName = _clean(typeof c === 'string' ? c : (c?.name || c?.provider)).toLowerCase()
      return cName && (carrierName.includes(cName) || cName.includes(carrierName))
    })
  ) {
    return true
  }

  // 4. IPA / medical group.
  const patientIpaId = _clean(insurance.ipaId)
  if (patientIpaId) {
    return ipas.some((ipa) => _clean(typeof ipa === 'string' ? ipa : ipa?.id) === patientIpaId)
  }

  return false
}

// Tri-state for surfaces that tag + sort rather than hard-filter (e.g. ShareScreen).
//   'confirmed' — in-network
//   'no_match'  — patient has insurance and the doctor lists plans, but none match
//   'unknown'   — not enough data to judge (no accepted plans / no patient coverage)
export function getInsuranceMatchType(doctor, insurance) {
  if (!patientHasInsurance(insurance)) return 'unknown'
  const { providers, acceptAll, payerIds } = normalizeDoctorInsurance(doctor)
  if (acceptAll) return 'confirmed'
  if (isDoctorInNetwork(doctor, insurance)) return 'confirmed'
  if ((providers && providers.length) || (payerIds && payerIds.length)) return 'no_match'
  return 'unknown'
}
